import { Box, Paper, Stack, Typography } from '@mui/material';
import React from 'react';

import { BORDER_GRAY } from '../../../theme';
import {
  FieldName,
  FieldNamesToggle,
  ShowFieldNamesContext,
} from './FieldNames';
import { StepList } from './StepList';
import type { LaunchStep, StepStatus } from './types';

type Props = {
  title: string;
  steps: LaunchStep[];
  statuses: StepStatus[];
  currentIndex: number;
  readOnly: boolean;
  onSelect: (index: number) => void;
  /** Buttons under the step, with an optional note on the right. */
  actions: React.ReactNode;
  note?: string;
  aside: React.ReactNode;
  children: React.ReactNode;
};

/** Step list, the current step's card and the right column. */
export const LaunchLayout: React.FC<Props> = ({
  title,
  steps,
  statuses,
  currentIndex,
  readOnly,
  onSelect,
  actions,
  note,
  aside,
  children,
}) => {
  const [showFieldNames, setShowFieldNames] = React.useState(false);
  const step = steps[currentIndex];

  return (
    <ShowFieldNamesContext.Provider value={showFieldNames}>
      <Box sx={{ maxWidth: 1320, mx: 'auto', py: 2.5 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" component="h1">
            {title}
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <FieldNamesToggle
              checked={showFieldNames}
              onChange={setShowFieldNames}
            />
          </Box>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            alignItems: 'start',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: '220px minmax(0, 1fr)',
              lg: '240px minmax(0, 1fr) 300px',
            },
          }}
        >
          <Box component="nav" aria-label="Setup steps">
            <StepList
              steps={steps}
              statuses={statuses}
              currentIndex={currentIndex}
              readOnly={readOnly}
              onSelect={onSelect}
            />
          </Box>
          <Paper variant="outlined" sx={{ borderRadius: 2.5 }}>
            <Box
              sx={{
                px: 3,
                pt: 2.75,
                pb: 2,
                borderBottom: `1px solid ${BORDER_GRAY}`,
              }}
            >
              <Typography
                variant="overline"
                color="text.disabled"
                fontWeight={600}
              >
                Step {currentIndex + 1} of {steps.length}
              </Typography>
              <Typography variant="h5" component="h2" fontWeight={600}>
                {step.title}
              </Typography>
              {step.intro && (
                <Typography variant="body2" color="text.disabled">
                  {step.intro}
                </Typography>
              )}
              {step.fieldNames && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 1.5 }}>
                  {step.fieldNames.map((name) => (
                    <FieldName key={name}>{name}</FieldName>
                  ))}
                </Stack>
              )}
            </Box>
            <Stack spacing={3} sx={{ p: 3 }}>
              {children}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER_GRAY}` }}
            >
              {actions}
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ ml: 'auto !important', textAlign: 'right' }}
              >
                {note}
              </Typography>
            </Stack>
          </Paper>
          <Box
            component="aside"
            aria-label="Setup status"
            sx={{ gridColumn: { md: '1 / -1', lg: 'auto' } }}
          >
            {aside}
          </Box>
        </Box>
      </Box>
    </ShowFieldNamesContext.Provider>
  );
};
